import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import objectSupport from 'dayjs/plugin/objectSupport';
import isBetween from 'dayjs/plugin/isBetween';
import _ from 'lodash';

import {
    GoBackButton,
    Text,
    ContainedButton,
    OverlapCard,
    StyledDialog,
    DeleteIcon2,
    OutlinedButton,
    ConfirmDialog,
} from '../../components/index';

import CreateSessionCard from '../../components/Provider/Appointment/CreateSessionCard';
import ProviderCard from '../../components/Provider/Appointment/ProviderCard';
import PatientCard from '../../components/Provider/Appointment/PatientCard';
import TotalCreditsCard from '../../components/Provider/Appointment/TotalCreditsCard';

import { providerPageWidth } from '../../constant';

import {
    createOne,
    editOne,
    deleteOne,
    GET_CALENDAR,
    GET_MEET,
    canStart,
} from '../../services/provider';
import { alertError, alertSuccess } from '../../redux/stateActions';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(objectSupport);
dayjs.extend(isBetween);

const creditCalculate = (startTime, endTime) => {
    /* 
        1on1 session
        first 30 minutes cost 100 credit
        after 30 minutes cost 2 credit per minute
      */
    const duration = endTime.diff(startTime, 'minute');

    if (duration < 0) return 0;

    let cost = 0;
    if (duration <= 30) {
        cost = 100;
    } else {
        const exceedDuration = duration - 30;
        cost = 100 + exceedDuration * 2;
    }

    return cost;
};

const CreateOneOnOne = ({ history, location }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const { id } = useParams();
    const matchEdit = useRouteMatch('/provider/calendar/one/edit/:id');

    useEffect(() => {
        const { search } = location;
        const queryParams = new URLSearchParams(search);
        const date = queryParams.get('date');
        const staff = queryParams.get('staff');

        if (date && dayjs.isDayjs(dayjs(date))) {
            setAppointmentDate(dayjs(date));
        }
        if (staff) {
            setSelectedStaff(staff);
        } else if (
            !_.isEmpty(user) &&
            !user.staff.deactivated &&
            user.staff.staff_role.name !== 'Admin'
        ) {
            setSelectedStaff(user.staff.id);
        }
    }, []);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentStartTime, setAppointmentStartTime] = useState('');
    const [appointmentEndTime, setAppointmentEndTime] = useState('');
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [credits, setCredits] = useState(0);
    const [calendar, setCalerdar] = useState({});
    const [creditsError, setCreditsError] = useState(false);
    const [overlapData, setOverlapData] = useState({});
    const [selectedStaff, setSelectedStaff] = useState('');

    const disabledEditMeet = useMemo(() => {
        return selectedPatients.some((p) => p.disabled);
    }, [selectedPatients]);

    const handleGoBack = () => {
        history.goBack();
    };

    /*--------------- Get Calendar ---------------*/
    const {
        data: getCalendarData,
        error: getCalendarError,
        loading: getCalendarLoading,
    } = useQuery(GET_CALENDAR, {
        variables: {
            meet: matchEdit ? id : undefined,
            staff: selectedStaff,
            date: !_.isNull(appointmentDate) && dayjs(appointmentDate).format('YYYY-MM-DD'),
        },
        skip: _.isEmpty(user) || _.isNull(appointmentDate) || !selectedStaff,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getCalendarError) {
            dispatch(alertError(`Cannot get calendar: ${getCalendarError.message}`));
        }

        if (getCalendarData && getCalendarData.calendars.length > 1) {
            dispatch(alertError(`More than one calendar found`));
            return;
        }

        if (getCalendarData) {
            if (_.isEmpty(getCalendarData.calendars)) {
                setCalerdar({});
            } else {
                setCalerdar(getCalendarData.calendars[0]);
            }
        }
    }, [getCalendarData, getCalendarError]);

    /*--------------- Get Meet for edit and duplicate ---------------*/
    const {
        data: getMeetData,
        error: getMeetError,
        loading: getMeetLoading,
    } = useQuery(GET_MEET, {
        variables: { id: id },
        skip: !id,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getMeetError) {
            dispatch(alertError(`Cannot get Meet data: ${getMeetError.message}`));
        }

        if (getMeetData && _.isEmpty(getMeetData.meet)) {
            dispatch(alertError(`Meet not found`));
            handleGoBack();
        }

        if (getMeetData && !_.isEmpty(getMeetData.meet)) {
            const { type, name, description, staff, sessions, startDate, endTime } =
                getMeetData.meet;

            if (type !== 'one') {
                handleGoBack();
                return;
            }

            const endDateTime = dayjs(startDate).set({
                hour: endTime.split(':')[0],
                minute: endTime.split(':')[1],
            });

            if (matchEdit && dayjs().isAfter(endDateTime, 'minute')) {
                handleGoBack();
                return;
            }

            setName(name);
            setDescription(description);
            setSelectedStaff(staff.id);

            if (matchEdit) {
                setAppointmentDate(dayjs(startDate));
            }

            const sortedSessions = Array.from(sessions).sort((a, b) =>
                dayjs()
                    .set({
                        hour: a.startTime.split(':')[0],
                        minute: a.startTime.split(':')[1],
                    })
                    .isAfter(
                        dayjs().set({
                            hour: b.startTime.split(':')[0],
                            minute: b.startTime.split(':')[1],
                        })
                    )
                    ? 1
                    : -1
            );

            const newSelectedPatients = [];

            // set patient
            sortedSessions.forEach((session) => {
                const { startTime, endTime, patient_summaries, id } = session;

                if (patient_summaries.length === 0 || !patient_summaries[0]?.patient?.user) return;

                const newStartTime = dayjs().set({
                    hour: startTime.split(':')[0],
                    minute: startTime.split(':')[1],
                });

                const newEndTime = dayjs().set({
                    hour: endTime.split(':')[0],
                    minute: endTime.split(':')[1],
                });

                newSelectedPatients.push({
                    startTime: newStartTime.format('HH:mm'),
                    endTime: newEndTime.format('HH:mm'),
                    credit: creditCalculate(newStartTime, newEndTime),
                    id: patient_summaries[0].patient.id,
                    HNCode: patient_summaries[0].patient.HNCode,
                    user: patient_summaries[0].patient.user,
                    sessionId: matchEdit ? id : undefined,
                    disabled: matchEdit && patient_summaries[0]?.checkinTime,
                });
            });
            setSelectedPatients(newSelectedPatients);
        }
    }, [getMeetError, getMeetData]);

    useEffect(() => {
        /*--------- calculate startTime endTime ----------*/
        let minStartTime = '';
        let maxEndTime = '';
        selectedPatients.forEach((patient) => {
            if (patient.startTime !== '') {
                if (minStartTime === '') {
                    minStartTime = patient.startTime;
                }
                if (
                    dayjs()
                        .set({
                            hour: minStartTime.split(':')[0],
                            minute: minStartTime.split(':')[1],
                        })
                        .isAfter(
                            dayjs().set({
                                hour: patient.startTime.split(':')[0],
                                minute: patient.startTime.split(':')[1],
                            })
                        )
                ) {
                    minStartTime = patient.startTime;
                }
            }

            if (patient.endTime !== '') {
                if (maxEndTime === '') {
                    maxEndTime = patient.endTime;
                }
                if (
                    dayjs()
                        .set({
                            hour: maxEndTime.split(':')[0],
                            minute: maxEndTime.split(':')[1],
                        })
                        .isBefore(
                            dayjs().set({
                                hour: patient.endTime.split(':')[0],
                                minute: patient.endTime.split(':')[1],
                            })
                        )
                ) {
                    maxEndTime = patient.endTime;
                }
            }
        });

        setAppointmentStartTime(minStartTime);
        setAppointmentEndTime(maxEndTime);

        /*--------- Sum Credits ----------*/
        const newCredits = 0;
        const sumWithInitial = selectedPatients.reduce(
            (previousValue, obj) => previousValue + obj.credit,
            newCredits
        );
        setCredits(sumWithInitial);
    }, [selectedPatients]);

    useEffect(() => {
        const checkCredits = async () => {
            try {
                const res = await canStart({ id: user.staff.hospital.id, amount: credits });

                if (res.status === 200) {
                    if (res.data.canStart === true) {
                        setCreditsError(false);
                    } else {
                        setCreditsError(true);
                    }
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                dispatch(alertError(`Cannot check credits: ${error.message}`));
            }
        };

        if (credits > 0) {
            checkCredits();
        }
    }, [credits]);

    const handleSelectStaff = (id) => {
        setSelectedStaff(id);
        setErrors(errors.filter((error) => error.property !== 'selectedProvider'));
    };

    const handleAddPatient = (patientArr) => {
        setSelectedPatients(patientArr);
        setErrors(errors.filter((error) => error.property !== 'selectedPatients'));
    };

    const handleRemovePatient = (id, sessionId) => {
        setSelectedPatients(selectedPatients.filter((patient) => patient.id !== id));
        setErrors(
            errors.filter(
                (error) =>
                    error.property !== 'selectedPatients' &&
                    error.property !== 'appointmentStartTime' &&
                    error.property !== 'appointmentEndTime'
            )
        );
    };

    const handleDateChange = (date) => {
        setAppointmentDate(date);
        setErrors(errors.filter((error) => error.property !== 'appointmentDate'));
    };

    const handleStartTimeChange = (time) => {
        setAppointmentStartTime(time);
        setErrors(errors.filter((error) => error.property !== 'appointmentStartTime'));
    };
    const handleEndTimeChabge = (time) => {
        setAppointmentEndTime(time);
        setErrors(errors.filter((error) => error.property !== 'appointmentEndTime'));
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
    };

    const handlePatientTimeChange = (id, type, time) => {
        const newSelectedPatients = [...selectedPatients];
        const patientIndex = newSelectedPatients.findIndex((p) => p.id === id);
        newSelectedPatients[patientIndex][type] = time;

        if (
            newSelectedPatients[patientIndex].startTime !== '' &&
            newSelectedPatients[patientIndex].endTime !== ''
        ) {
            const startTime = dayjs().set({
                hour: newSelectedPatients[patientIndex].startTime.split(':')[0],
                minute: newSelectedPatients[patientIndex].startTime.split(':')[1],
            });

            const endTime = dayjs().set({
                hour: newSelectedPatients[patientIndex].endTime.split(':')[0],
                minute: newSelectedPatients[patientIndex].endTime.split(':')[1],
            });

            newSelectedPatients[patientIndex].credit = creditCalculate(startTime, endTime);
        }

        setSelectedPatients(newSelectedPatients);

        setErrors(
            errors.filter(
                (error) =>
                    error.property !== `${id}-startTime` &&
                    error.property !== `${id}-endTime` &&
                    error.property !== 'appointmentStartTime' &&
                    error.property !== 'appointmentEndTime'
            )
        );
    };

    const onCancelConfirm = () => {
        setOverlapData({});
    };

    const handleSubmit = async () => {
        setOverlapData({});

        const data = {
            id: id,
            hospital: user.staff.hospital.id,
            name: name.trim(),
            description: description.trim(),
            staff: selectedStaff,
            patients: selectedPatients.map((patient) => ({
                id: patient.id,
                startTime: dayjs()
                    .set({
                        hour: patient.startTime.split(':')[0],
                        minute: patient.startTime.split(':')[1],
                    })
                    .format('HH:mm:ss.SSS'),
                endTime: dayjs()
                    .set({
                        hour: patient.endTime.split(':')[0],
                        minute: patient.endTime.split(':')[1],
                    })
                    .format('HH:mm:ss.SSS'),
                sessionId: patient?.sessionId,
            })),
            date: dayjs(appointmentDate).format('YYYY-MM-DD'),
            startTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentStartTime.split(':')[0])
                .set('minute', appointmentStartTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
            endTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentEndTime.split(':')[0])
                .set('minute', appointmentEndTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
        };

        try {
            setLoading(true);

            let res;

            if (matchEdit) {
                res = await editOne(data);
            } else {
                res = await createOne(data);
            }

            console.log('res', res);

            if (res?.status === 200) {
                dispatch(alertSuccess(`1 on 1 session ${matchEdit ? 'Edited' : 'Created'}`));

                if (matchEdit) {
                    history.push(`/provider/session/one/${id}`);
                } else {
                    history.push(`/provider/calendar`);
                }
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);

            dispatch(
                alertError(
                    `Cannot ${matchEdit ? 'edit' : 'create'} 1on1 Session: ${JSON.stringify(
                        error.response?.data?.message
                    )}`
                )
            );
        }
    };

    const handleSave = async () => {
        const newErrors = [];

        if (appointmentDate === null) {
            newErrors.push({
                property: 'appointmentDate',
                message: 'Please provide a valid Date.',
            });
        }

        if (appointmentStartTime === '') {
            newErrors.push({
                property: 'appointmentStartTime',
                message: 'Please provide a valid Start Time.',
            });
        }
        if (appointmentEndTime === '') {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'Please provide a valid End Time.',
            });
        }

        if (selectedStaff === '') {
            newErrors.push({
                property: `selectedProvider`,
                message: 'Please select provider.',
            });
        }

        if (
            dayjs()
                .set({
                    hour: appointmentStartTime.split(':')[0],
                    minute: appointmentStartTime.split(':')[1],
                })
                .isSameOrAfter(
                    dayjs().set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    }),
                    'minute'
                )
        ) {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'End Time must be after Start Time.',
            });
        }

        if (_.isEmpty(selectedPatients)) {
            newErrors.push({
                property: 'selectedPatients',
                message: 'Please provide a valid Client.',
            });
        } else {
            selectedPatients.forEach((patient) => {
                if (patient?.disabled) return;

                if (patient.startTime === '') {
                    newErrors.push({
                        property: `${patient.id}-startTime`,
                        message: 'Please provide a valid Start Time.',
                    });
                }
                if (patient.endTime === '') {
                    newErrors.push({
                        property: `${patient.id}-endTime`,
                        message: 'Please provide a valid End Time.',
                    });
                }

                if (
                    dayjs()
                        .set({
                            hour: patient.startTime.split(':')[0],
                            minute: patient.startTime.split(':')[1],
                        })
                        .isSameOrAfter(
                            dayjs().set({
                                hour: patient.endTime.split(':')[0],
                                minute: patient.endTime.split(':')[1],
                            }),
                            'minute'
                        )
                ) {
                    newErrors.push({
                        property: `${patient.id}-endTime`,
                        message: 'End Time must be after Start Time.',
                    });
                }

                if (
                    appointmentDate !== null &&
                    (dayjs(appointmentDate)
                        .set({
                            hour: patient.startTime.split(':')[0],
                            minute: patient.startTime.split(':')[1],
                        })
                        .isSameOrBefore(dayjs()) ||
                        dayjs(appointmentDate)
                            .set({
                                hour: patient.endTime.split(':')[0],
                                minute: patient.endTime.split(':')[1],
                            })
                            .isSameOrBefore(dayjs()))
                ) {
                    newErrors.push({
                        property: `${patient.id}-endTime`,
                        message: 'Session must be in the future.',
                    });
                }

                selectedPatients.forEach((p) => {
                    if (patient.id !== p.id) {
                        if (
                            dayjs()
                                .set({
                                    hour: patient.startTime.split(':')[0],
                                    minute: patient.startTime.split(':')[1],
                                })
                                .isBetween(
                                    dayjs().set({
                                        hour: p.startTime.split(':')[0],
                                        minute: p.startTime.split(':')[1],
                                    }),
                                    dayjs().set({
                                        hour: p.endTime.split(':')[0],
                                        minute: p.endTime.split(':')[1],
                                    }),
                                    'hour',
                                    '()'
                                ) ||
                            dayjs()
                                .set({
                                    hour: patient.endTime.split(':')[0],
                                    minute: patient.endTime.split(':')[1],
                                })
                                .isBetween(
                                    dayjs().set({
                                        hour: p.startTime.split(':')[0],
                                        minute: p.startTime.split(':')[1],
                                    }),
                                    dayjs().set({
                                        hour: p.endTime.split(':')[0],
                                        minute: p.endTime.split(':')[1],
                                    }),
                                    'hour',
                                    '()'
                                )
                        ) {
                            newErrors.push({
                                property: `${patient.id}-endTime`,
                                message: 'Time overlap',
                            });
                            newErrors.push({
                                property: `${p.id}-endTime`,
                                message: 'Time overlap',
                            });
                        }
                    }
                });
            });
        }

        setErrors(newErrors);
        if (!_.isEmpty(newErrors)) return;

        if (!_.isEmpty(calendar)) {
            // check meet time overlap
            const { meets } = calendar;

            const startTimeOverlap = meets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentStartTime.split(':')[0],
                        minute: appointmentStartTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'minute',
                        '[]'
                    )
            );
            const endTimeOverlap = meets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'minute',
                        '[]'
                    )
            );
            if (startTimeOverlap) {
                setOverlapData(startTimeOverlap);
                return;
            }
            if (endTimeOverlap) {
                setOverlapData(endTimeOverlap);
                return;
            }
        }

        handleSubmit();
    };

    const [openDelete, setOpenDelete] = useState(false);

    const onDelete = () => {
        setOpenDelete(true);
    };

    const onCancelDelete = () => {
        setOpenDelete(false);
    };

    const onConfirmDelete = async () => {
        try {
            setLoading(true);

            let res = await deleteOne(id);

            console.log('res', res);

            if (res?.status === 200) {
                dispatch(alertSuccess(`1 on 1 session Deleted`));

                history.push(`/provider/calendar`);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            console.log('error', error.response);
            setLoading(false);

            dispatch(
                alertError(
                    `Cannot delete 1on1 Session: ${JSON.stringify(error.response?.data?.message)}`
                )
            );
        }
    };

    return (
        <div
            style={{
                padding: '41px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '20px',
            }}
        >
            <ConfirmDialog
                open={openDelete}
                title="Are you sure you want to delete this Session?"
                contentText="Please confirm if you want to proceed with the deletion."
                cancelText="Cancel"
                onCancel={onCancelDelete}
                confirmText="Yes, Delete"
                onConfirm={onConfirmDelete}
                loading={loading}
            />
            <StyledDialog
                scroll="body"
                open={!_.isEmpty(overlapData)}
                onClose={onCancelConfirm}
                aria-labelledby="warningDialog"
                aria-describedby="warning dialog"
                PaperProps={{ style: { borderRadius: '16px' } }}
            >
                <OverlapCard
                    data={overlapData}
                    onCancelConfirm={onCancelConfirm}
                    onConfirm={handleSubmit}
                />
            </StyledDialog>
            <GoBackButton $text onClick={handleGoBack} />
            <Text $fontSize="20px" $bold>
                {`${matchEdit ? 'Edit' : 'Create'} 1 on 1 session`}
            </Text>
            <CreateSessionCard
                errors={errors}
                loading={loading || getCalendarLoading || getMeetLoading}
                disabledDate={disabledEditMeet}
                disabledTime
                appointmentDate={appointmentDate}
                handleDateChange={handleDateChange}
                appointmentStartTime={appointmentStartTime}
                handleStartTimeChange={handleStartTimeChange}
                appointmentEndTime={appointmentEndTime}
                handleEndTimeChabge={handleEndTimeChabge}
                name={name}
                handleNameChange={handleNameChange}
                description={description}
                handleDescriptionChange={handleDescriptionChange}
            />
            {user.staff.staff_role.name !== 'Provider' && (
                <ProviderCard
                    errors={errors}
                    loading={loading || getCalendarLoading || getMeetLoading}
                    selectedStaff={selectedStaff}
                    handleSelectStaff={handleSelectStaff}
                    disabled={disabledEditMeet}
                />
            )}
            <PatientCard
                layout="ONE_ON_ONE"
                errors={errors}
                loading={loading || getCalendarLoading || getMeetLoading}
                selectedPatients={selectedPatients}
                handleAddPatient={handleAddPatient}
                handlePatientTimeChange={handlePatientTimeChange}
                handleRemovePatient={handleRemovePatient}
            />
            <TotalCreditsCard data={credits} error={creditsError} />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: matchEdit ? 'space-between' : 'flex-end',
                }}
            >
                {matchEdit && (
                    <OutlinedButton
                        $color="pink"
                        $width="99px"
                        $borderWidth="1.5px"
                        $padding="0"
                        onClick={onDelete}
                        disabled={disabledEditMeet}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: '3px',
                            }}
                        >
                            <DeleteIcon2
                                width="20px"
                                height="20px"
                                fill={disabledEditMeet ? 'gray4' : 'pink'}
                            />
                            <Text $color={disabledEditMeet ? 'gray4' : 'pink'}>Delete</Text>
                        </div>
                    </OutlinedButton>
                )}
                <ContainedButton
                    style={{ height: '32px', width: '93px' }}
                    onClick={handleSave}
                    disabled={
                        name === '' ||
                        description === '' ||
                        _.isEmpty(selectedPatients) ||
                        (user.staff.staff_role.name === 'Provider' && user.staff.deactivated)
                    }
                    loading={loading || getCalendarLoading || getMeetLoading}
                >
                    Next
                </ContainedButton>
            </div>
        </div>
    );
};

CreateOneOnOne.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default CreateOneOnOne;
